import { RegisterFormRequest } from '../../components/RecordingRequestForm'
import { RegisterUserFormRequest } from '../../components/RecordingUserRequestForm'
import { dbOperatorUrl, resourceManagerUrl } from '../../utils/config'
import { Resource, CreateUser, Analytic, DataFoundationProject, Agent } from '../../utils/types/dataStructures'
import { SolutionDesign, SolutionDesignGcp } from '../../utils/types/vmRequests'
import { apiCaller } from '../apiCaller'
import { secureApiCall } from '../secureApiClient'

export interface DeveloperUser {
  email: string
}

export async function getDevelopers (): Promise<DeveloperUser[]> {
  const response = await secureApiCall<unknown>({
    targetUrl: `${dbOperatorUrl}/developer`,
    method: 'GET',
    subscriptionType: 'all'
  })
  if (response.status !== 200 || response.error != null) {
    throw new Error(`Failed to load internal users: ${response.error ?? response.status}`)
  }
  if (!Array.isArray(response.data)) {
    throw new TypeError('Invalid developer response: expected an array of email records')
  }

  const usersByEmail = new Map<string, DeveloperUser>()
  response.data.forEach((user: unknown) => {
    if (user == null || typeof user !== 'object' || !('email' in user) || typeof user.email !== 'string' || user.email.trim() === '') {
      throw new TypeError('Invalid developer response: every record must contain a non-empty email')
    }
    const email = user.email.trim().toLowerCase()
    usersByEmail.set(email, { email })
  })
  return Array.from(usersByEmail.values())
}

/**
 *
 * @param request
 * @returns
 */

export async function saveFormRequest (request: RegisterFormRequest): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/request`,
    method: 'POST',
    data: JSON.stringify(request)
  })

  return response.json()
}

export async function saveUserFormRequest (request: RegisterUserFormRequest): Promise<any> {
  console.log('Hit API!...')
  const response = await apiCaller({
    url: `${dbOperatorUrl}/request`,
    method: 'POST',
    data: JSON.stringify(request)
  })

  console.log('Return response: ', response)
  return response.json()
}

export async function createNewUserEntry (request: CreateUser): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/user`,
    method: 'POST',
    data: JSON.stringify(request)
  })
  return response.json()
}

/**
 *
 * @returns
 */
export async function getRequests (): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/request`,
    method: 'GET'
  })

  return response.json()
}

/**
 *
 * @returns
 */
export async function getRequestsById (id: number): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/request/${id}`,
    method: 'GET'
  })

  return response.json()
}

export async function getRequestsByType (requestType: string): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/request/type/${requestType}`,
    method: 'GET'
  })

  return response.json()
}

export async function getRequestsByFiltersPost (requestType: string, payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/request/report/${requestType}`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}
export async function getResourcesByTypePost (requestType: string, payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/resource/report/${requestType}`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export async function getResourceReportAll (payload: any = {}): Promise<any[]> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/resource/report/all`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.log(error)
    return []
  }
}

export async function getRequestReportAll (payload: any = {}): Promise<any[]> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/request/report/all`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.log(error)
    return []
  }
}
export async function getResources (): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/resource`,
    method: 'GET'
  })
  return response.json()
}
export async function getResourceById (id: number): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/resource/${id}`,
    method: 'GET'
  })
  return response.json()
}

export async function getApprovals (): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/approval`,
    method: 'GET'
  })
  return response.json()
}
/**
 *
 * @returns
 */
export async function getPlatforms (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/platform`,
      method: 'GET'
    })

    const data = await response.json()

    if (Array.isArray(data)) {
      // Sorting the data array by the 'buName' property
      data.sort((a, b) => a.buName.localeCompare(b.buName))
      return data
    } else {
      // The case when data is not an array
      return []
    }
  } catch (error) {
    console.error('❌ db operator for platform values call has failed:', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getPlatformSysid (queryParams: Record<string, number | string>): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/snplatform`
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const fullUrl = `${url}?${queryString}`

    const response = await apiCaller({
      url: fullUrl,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

/**
 *
 * @returns
 */
export async function getServices (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/snservice`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.error('❌ db operator for getServices call has failed:', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getRelationshipDetails (queryParams: Record<string, number | string>): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/snrelationship`
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const fullUrl = `${url}?${queryString}`

    const response = await apiCaller({
      url: fullUrl,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function getApplicationDetails (queryParams: Record<string, number | string>): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/snapplication`
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const fullUrl = `${url}?${queryString}`

    const response = await apiCaller({
      url: fullUrl,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

/**
 *
 * @param string
 * @returns
 */
export async function getUserMappingDetails (email: string): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/user?email=${email}`,
      method: 'GET'
    })

    const responsePayload = await response.json()
    const userMap = new Map<string, any[]>()

    responsePayload?.resources.forEach((element: Resource) => {
      const resourceArray = userMap.has(element.requestType) ? userMap.get(element.requestType) : [] as any[]
      resourceArray?.push(element.resourceDetails)

      if (resourceArray !== undefined) {
        userMap.set(element.requestType, resourceArray)
      }
    })
    if (responsePayload !== null) {
      userMap.set('User_id', responsePayload.id)
    }
    return userMap === null ? new Map<string, any[]>() : userMap
  } catch (error) {
    console.error('❌ user mapping call has failed for email:', email, error)
    const userMap = new Map<string, any[]>()
    return userMap
    // api failure should not prevent page from loading
  }
}

export async function getUserDetailsById (id: number): Promise<boolean | null> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/user/${id}`,
      method: 'GET'
    })
    const userDetails = await response.json()

    if (!userDetails) {
      throw new Error(`User with ID ${id} not found`)
    }
    // Convert exptsandcsread to boolean
    const exptsAndCsRead = userDetails.exptsandcsread === 'yes'
    return exptsAndCsRead
  } catch (error) {
    console.error('Error fetching user details:', error)
    return null
  }
}

export async function getUserDetailsByEmail (email: string): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/user?email=${email}`,
      method: 'GET'
    })

    const userDetails = await response.json()

    if (!userDetails) {
      throw new Error(`User with email ${email} not found`)
    }
    return userDetails.id
  } catch (error) {
    console.error('Error fetching user details by email:', error)
    return null
  }
}

export interface ParkingGroupUser {
  id: number
  name: string
  email: string
}

export async function getParkingGroupUsers (): Promise<ParkingGroupUser[]> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/user`,
      method: 'GET'
    })

    const users = await response.json()

    if (!Array.isArray(users)) {
      return []
    }

    return users.map((user: any) => ({
      id: Number(user.id),
      name: String(user.name ?? ''),
      email: String(user.email ?? '')
    }))
      .filter((user: ParkingGroupUser) => Number.isFinite(user.id) && user.name !== '' && user.email !== '')
  } catch (error) {
    console.error('Error fetching parking group users:', error)
    return []
  }
}

/**
 *
 * @param string
 * @returns
 */
export async function getGcpNetworkDetails (platform: string): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/gcpnetworks?platform=${platform}`,
      method: 'GET'
    })

    const responsePayload = await response.json()
    return responsePayload
  } catch (error) {
    console.log('gcp networks  call has failed', error)
    return []
  }
}
/**
 *
 * @param string
 * @returns
 */
export async function getServiceQueryDetails (queryParams: Record<string, number | string>): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/snservice`
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const fullUrl = `${url}?${queryString}`

    const response = await apiCaller({
      url: fullUrl,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

// Get Azure Subscription Details based on the subscription <name>
export async function getAzureSubscriptionSysId (queryParams: Record<string, number | string>): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/snazuresubscription`
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')
    const fullUrl = `${url}?${queryString}`

    const response = await apiCaller({
      url: fullUrl,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

// Get Azure Subscription Details based on the subscription <name>
export async function updateResourceTable (inputpayload: any, id: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/resource/${id}`

    const response = await apiCaller({
      url,
      method: 'PATCH',
      data: JSON.stringify(inputpayload)
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function updateUserApproval (id: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/user/${id}`
    const inputPayload = {
      expts_and_csread: 'yes'
    }

    const response = await apiCaller({
      url,
      method: 'PATCH',
      data: JSON.stringify(inputPayload)
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function getAllReport (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/report`,
      method: 'GET'
    })

    const reportDetails = await response.json()

    if (reportDetails === null) {
      throw new Error('No data found')
    } else {
      return reportDetails
    }
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function getReport (userId: number): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/report?userId=${userId}`,
      method: 'GET'
    })

    const reportDetails = await response.json()

    if (!reportDetails) {
      throw new Error(`User with ID ${userId} not found`)
    } else {
      return reportDetails
    }
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function getPlatformContact (platformName: string, mode: number): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/subscription-audit?platformName=${platformName}&mode=${mode}`,
      method: 'GET'
    })

    const contactDetails = await response.json()

    if (!contactDetails) {
      throw new Error(`Platform with name, ${platformName}, not found`)
    } else {
      return contactDetails
    }
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return []
  }
}

export async function getSubProjAudit (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/subscription-audit`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.error('❌ db operator for getSubProjAudit call has failed:', error)
    return []// api failure should not prevent page from loading
  }
}

export async function patchSubProjAudit (data: any, id: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/subscription-audit/${id}`

    const response = await apiCaller({
      url,
      method: 'PATCH',
      data // ✅ Let apiCaller/secureApiCall handle stringification
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while patching values', error)
    return []
  }
}

export async function createSubProjAudit (data: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/subscription-audit`,
      method: 'POST',
      subscriptionType: 'all',
      body: data
    })

    if (response.error || response.status !== 200) {
      throw new Error(`Failed to create SubProjAudit: ${response.error || response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while creating SubProjAudit entry', error)
    throw error
  }
}

export async function updateRequestStatus (requestId: number, status: string): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/request/${requestId}`

    const response = await secureApiCall({
      targetUrl: url,
      method: 'PATCH',
      subscriptionType: 'all',
      body: { status }
    })

    if (response.error || response.status !== 200) {
      throw new Error(`Failed to update request status: ${response.error || response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while updating request status', error)
    throw error
  }
}

export async function getRequestById (requestId: number): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/request/${requestId}`,
      method: 'GET',
      subscriptionType: 'all'
    })

    if (response.error || response.status !== 200) {
      throw new Error(`Failed to get request: ${response.error || response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching request', error)
    throw error
  }
}

export async function getPlatformsMgmt (platformId: number): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/platform/${platformId}`,
      method: 'GET'
    })

    const data = await response.json()

    return data
  } catch (error) {
    console.log('Error fetching platform by ID:', error)
    return null
  }
}

// >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> TESTING LOAD / SAVE feature

export async function saveBuildSheet (request: SolutionDesign | SolutionDesignGcp): Promise<any> {
  try {
    // Log the request payload
    // console.log('Saving build sheet with request:', JSON.stringify(request, null, 2))

    const response = await apiCaller({
      url: `${dbOperatorUrl}/solution-design`,
      method: 'POST',
      data: JSON.stringify(request, null, 2)
    })

    // Log the response status
    // console.log('Response status:', response.status)

    if (!response.ok) {
      // Log the full error details including status and response body
      const errorResponse = await response.json()
      console.error('Failed to save build sheet. Response details:', {
        status: response.status,
        statusText: response.statusText,
        errorResponse
      })
      throw new Error(`Failed to save: ${response.statusText}`)
    }

    // Log the response body
    const jsonResponse = await response.json()
    // console.log('Response data:', JSON.stringify(jsonResponse, null, 2))

    return jsonResponse
  } catch (error: any) {
    // Log both the error message and any additional error details
    console.error('Error saving build sheet:', error.message)
    console.error('Detailed error:', error)

    throw error // Rethrow to allow handling at a higher level if needed
  }
}

export async function loadSolutionList (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/solution-design`, // Assuming this returns all designs
      method: 'GET'
    })

    if (!response.ok) {
      throw new Error(`Failed to load solution designs: ${response.statusText}`)
    }

    const jsonResponse = await response.json()

    // Filter the response to only include id and solution_name
    const filteredSolutions = jsonResponse.map((design: any) => ({
      id: design.id,
      solution_name: design.solutionName,
      cspType: design.cspType,
      remarks: design.remarks,
      solutionStatus: design.solutionStatus
    }))

    return filteredSolutions // Return filtered solution list
  } catch (error: any) {
    console.error('Error loading solution designs:', error.message)
    throw error
  }
}

export async function loadBuildSheet (id: string): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/solution-design/${id}`, // Fetch detailed data for selected solution
      method: 'GET'
    })

    if (!response.ok) {
      throw new Error(`Failed to load solution design: ${response.statusText}`)
    }

    const jsonResponse = await response.json()

    // console.log(jsonResponse)

    return jsonResponse // Return detailed data
  } catch (error: any) {
    console.error('Error loading solution design:', error.message)
    throw error
  }
}

export async function updateBuildSheet (id: number, payload: any): Promise<any> {
  try {
    // Structure the data as required
    // const dataToSend = {
    //   payload // Wrap payload within a 'payload' field
    // }

    const dataToSend = {
      payload: payload.formData, // Map formData to payload column
      remarks: payload.remarks // Map remarks to remarks column
    }

    // console.log('Updating build sheet with request:', JSON.stringify(dataToSend, null, 2))

    const response = await apiCaller({
      url: `${dbOperatorUrl}/solution-design/${id}`, // id is a number here
      method: 'PATCH',
      data: JSON.stringify(dataToSend)
    })

    // console.log('Response status: ', response.status)

    if (!response.ok) {
      const errorResponse = await response.json()
      console.error('Failed to update build sheet. Response details:', {
        status: response.status,
        statusText: response.statusText,
        errorResponse
      })
      throw new Error(`Failed to update: ${response.statusText}`)
    }

    const jsonResponse = await response.json()
    // console.log('Response data:', JSON.stringify(jsonResponse, null, 2))

    return jsonResponse
  } catch (error: any) {
    console.error('Error updating build sheet:', error.message)
    console.error('Detailed error:', error)
    throw error
  }
}

// export async function updateSolutionStatus (id: string, status: string): Promise<void> {
//   try {
//     await apiCaller({
//       url: `${dbOperatorUrl}/solution/${id}/status`, // Adjust the URL as needed
//       method: 'PATCH',
//       data: JSON.stringify({ solution_status: status }) // Send the new status
//     })
//   } catch (error) {
//     console.error('Error updating solution status:', error)
//     // Handle error as needed (e.g., log, alert user)
//   }
// }
export async function getAllHaasOrganisersSponsors (): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/haasorganiserssponsors`,
    method: 'GET'
  })
  return response.json()
}
export async function createSpecialSurpriseSurveys (payload: any): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/specialsurprisesurvey`,
      method: 'POST',
      data: JSON.stringify(payload)
    })
    return response.json()
  } catch (error) {
    console.log(error)
  }
}

export async function getAllAnalytics (): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic`,
    method: 'GET'
  })
  // return analyticData
  return response.json()
}
export async function getPaveViewAnalytics (isIncludeDev: any, startdate: any, enddate: any): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?analytics=pageview&includeDev=${isIncludeDev}&startdate=${startdate}&enddate=${enddate}`,
    method: 'GET'
  })
  return response.json()
}
export async function getSessionAnalytics (isIncludeDev: any, startdate: any, enddate: any): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?analytics=session&includeDev=${isIncludeDev}&startdate=${startdate}&enddate=${enddate}`,
    method: 'GET'
  })
  return response.json()
}
export async function getAnalyticsBySessionId (sessionId: any): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?analytics=sessiondetails&sessionid=${sessionId}`,
    method: 'GET'
  })
  return response.json()
}
export async function getAnalyticsByEmail (email: string): Promise<any> {
  const query = new URLSearchParams({ email: email.trim().toLowerCase() })
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?${query.toString()}`,
    method: 'GET'
  })
  return response.json()
}
export async function getAnalyticsByRequestidId (requestid: any): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?analytics=sessiondetails&requestid=${requestid}`,
    method: 'GET'
  })
  return response.json()
}
export async function getUserMatrics (isIncludeDev: any, startdate: any, enddate: any): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?analytics=user&includeDev=${isIncludeDev}&startdate=${startdate}&enddate=${enddate}`,
    method: 'GET'
  })
  return response.json()
}

export async function getResourceAnalyticsPost (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/analytic/report/resource`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export async function getRequestAnalyticsPost (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/analytic/report/request`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}
export async function getAnalyticsById (id: number): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic/${id}`,
    method: 'GET'
  })

  return response.json()
}
export async function getAnalyticsByParam (userName: string, pageName: string): Promise<any> {
  const response = await apiCaller({
    url: `${dbOperatorUrl}/analytic?user_name=${userName}&page_name=${pageName}`,
    method: 'GET'
  })

  return response.json()
}

export async function createAnalytics (payload: Analytic): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/analytic`,
      method: 'POST',
      data: JSON.stringify(payload)
    })
    return response.json()
  } catch (error) {
    console.log(error)
  }
}

export async function updateAnalytics (payload: any, id: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/analytic/${id}`

    const response = await apiCaller({
      url,
      method: 'PATCH',
      data: JSON.stringify(payload)
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return {}
  }
}

export async function getNotificationsById (userId: number): Promise<boolean | null> {
  // console.log('Entering getNotificationsById')
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/notification/user/${userId}`,
      method: 'GET'
    })
    const userNotifications = await response.json()

    // console.log('User notifications:', userNotifications)

    if (!userNotifications) {
      throw new Error(`User with ID ${userId} not found`)
    }

    return userNotifications
  } catch (error) {
    console.error('Error fetching user details:', error)
    return null
  }
}

export async function postNotificationsById (userId: number, notificationMessage: string): Promise<boolean | null> {
  console.log(`Entering getNotificationsById, ${userId}`)
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/notification`,
      method: 'POST',
      data: JSON.stringify({
        user_id: `${userId}`,
        message: `${notificationMessage}`,
        is_read: false
      })
    })
    const userNotifications = await response.json()

    // console.log('User notifications:', userNotifications)

    if (!userNotifications) {
      throw new Error(`User with ID ${userId} not found`)
    }

    return userNotifications
  } catch (error) {
    console.error('Error fetching user details:', error)
    return null
  }
}

export async function softDeleteNotificationsByNotificationId (notificationId: number, isRead: boolean): Promise<boolean | null> {
  // console.log('Entering softDeleteNotificationsByNotificationId')
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/notification/id/${notificationId}`,
      method: 'PATCH',
      data: JSON.stringify({ is_read: isRead })
    })

    // Check if the response status is not OK
    if (!response.ok) {
      const errorDetails = await response.json()
      console.error('Failed to soft delete notification:', {
        status: response.status,
        statusText: response.statusText //,
        // errorDetails
      })
      throw new Error(`Failed to soft delete notification with ID ${notificationId}: ${response.statusText}`)
    }

    const softDeleteNotification = await response.json()
    // console.log('Notifications soft deleted successfully:', softDeleteNotification)

    if (!softDeleteNotification) {
      throw new Error(`Notification with ID ${notificationId} not found`)
    }

    return softDeleteNotification
  } catch (error: any) {
    // Handle network errors or unexpected issues
    if (error.response) {
      console.error('API responded with an error:', {
        status: error.response.status,
        data: error.response.data
      })
    } else if (error.request) {
      console.error('No response received from the API:', error.request)
    } else {
      console.error('Unexpected error occurred:', error.message)
    }

    console.error('Error details:', error)
    return null
  }
}

export async function getHackathonDetailsByCode (uniqueCode: string): Promise<any | null> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/haas-registration/uniquecode/${uniqueCode}`,
      method: 'GET'
    })

    const data = await response.json()

    if (!data || data.length === 0) {
      throw new Error(`No data found for unique code: ${uniqueCode}`)
    }

    return data // returns full response array
  } catch (error) {
    console.error('Error fetching hackathon details by unique code:', error)
    return null
  }
}

// example API call to get event by organiser_code
export async function getEventTitleByOrganiserCode (
  organiserCode: string
): Promise<{ event_title: string, event_date: string, event_billing_id: string, event_csp: string } | null> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/haas-events`, // fetch all events
      method: 'GET'
    })

    const data = await response.json()

    if (!Array.isArray(data)) {
      console.error('Unexpected data format:', data)
      return null
    }

    const match = data.find((event: any) => event.organiser_code === organiserCode)

    if (match) {
      return {
        event_title: match.event_title,
        event_date: match.event_date,
        event_billing_id: match.event_billing_id,
        event_csp: match.event_csp
      }
    } else {
      return null
    }
  } catch (error) {
    console.error('Error fetching all events:', error)
    return null
  }
}

export async function saveDecommissioningRequest (request: { requester: string, geolocation_country?: string, VMBuildPayload: any }): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request`,
    method: 'POST',
    data: JSON.stringify(request)
  })

  return response.json()
}

export async function getDecommissioningRequest (request: { requester: string, geolocation_country?: string, VMBuildPayload: any }): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request`,
    method: 'GET',
    data: JSON.stringify(request)
  })

  return response.json()
}
export async function getDecommissioningRequestbyID (id: string): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/${id}`,
    method: 'GET'
  })

  return response.json()
}

export async function deleteDecommissioningRequest (id: string): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/${id}`,
    method: 'DELETE'
  })
  // If response body is empty, do not call .json()
  const text = await response.text()
  if (!text) return true
  try {
    return JSON.parse(text)
  } catch {
    return true
  }
}

export async function softdeleteDecommequest (id: string, softDelete: boolean): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/${id}`,
    method: 'PATCH',
    data: JSON.stringify({ soft_delete: softDelete })
  })
  // If response body is empty, do not call .json()
  const text = await response.text()
  if (!text) return true
  try {
    return JSON.parse(text)
  } catch {
    return true
  }
}

// Patch decommissioning request by id with arbitrary fields (e.g., soft_delete, status)
export async function patchDecommissioningRequest (id: string, payload: Record<string, any>): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/${id}`,
    method: 'PATCH',
    data: JSON.stringify(payload)
  })
  const text = await response.text()
  if (!text) return true
  try {
    return JSON.parse(text)
  } catch {
    return true
  }
}

export async function getDecommVMRequest (vmName: string): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/vm/${vmName}`,
    method: 'GET'
  })

  return response.json()
}

export async function getDecommRGRequest (rgName: string): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/rg/${rgName}`,
    method: 'GET'
  })

  return response.json()
}

export async function getDecommRequest (resourceType: string, resourceId: string): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/resource/${resourceType}/${resourceId}`,
    method: 'GET'
  })

  return response.json()
}

export async function getDecommVMRequestById (userId: number | undefined): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/user/${userId}`,
    method: 'GET'
  })

  return response.json()
}

export async function getDecommVMRequestByApprover (approverId: number): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request/approver/${approverId}`,
    method: 'GET'
  })

  return response.json()
}

export async function getAllDecommissionRequests (): Promise<any> {
  const response = await apiCaller({
    url: `${resourceManagerUrl}/request`,
    method: 'GET'
  })

  return response.json()
}

export async function getDataFoundationUsers (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/datafoundationuser`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getDataFoundationData (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/datafoundationproject`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getDataFoundationByAssetTagNumber (assetTagNumber: any): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/datafoundationproject/assettagnumber/${assetTagNumber}`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}
export async function getDataFoundationByITSG (itsg: any): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/datafoundationproject/itsg/${itsg}`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function createDataFoundationProject (payload: DataFoundationProject): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/datafoundationproject`,
      method: 'POST',
      data: JSON.stringify(payload)
    })
    return response.json()
  } catch (error) {
    console.log(error)
  }
}
export async function createSonarqubePlatform (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-organization`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })
    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to POST request: ${response.error ?? response.status}`)
    }
    return response.data
  } catch (error) {
    console.log(error)
  }
}
export async function updateSonarqubePlatform (payload: any): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/sonarqube-organization/${payload.id}`
    const response = await secureApiCall({
      targetUrl: url,
      method: 'PATCH',
      subscriptionType: 'all',
      body: payload
    })
    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to PATCH request: ${response.error ?? response.status}`)
    }
    return response.data
  } catch (error) {
    console.log('DB operator failed while updating request status', error)
    throw error
  }
}
export async function getSonarqubePlatforms (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-organization`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching request', error)
    throw error
  }
}

export async function deleteSonarqubePlatform (platformId: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/sonarqube-organization/${platformId}`
    const response = await secureApiCall({
      targetUrl: url,
      method: 'DELETE',
      subscriptionType: 'all'
    })
    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to DELETE request: ${response.error ?? response.status}`)
    }
    return response.data
  } catch (error) {
    console.log('DB operator failed while deleting request', error)
    throw error
  }
}

export async function createSonarqubeProject (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-project`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to POST request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error creating SonarQube project:', error)
    throw error
  }
}

export async function updateSonarqubeProject (id: any, payload: any): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/sonarqube-project/${id}`

    const response = await secureApiCall({
      targetUrl: url,
      method: 'PATCH',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to PATCH request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error updating SonarQube project:', error)
    throw error
  }
}

export async function getSonarqubeProjects (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-project`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to GET request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error fetching SonarQube projects:', error)
    throw error
  }
}

export async function deleteSonarqubeProject (projectId: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/sonarqube-project/${projectId}`

    const response = await secureApiCall({
      targetUrl: url,
      method: 'DELETE',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to DELETE request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error deleting SonarQube project:', error)
    throw error
  }
}
export async function getSonarqubeProjectUsers (projectId: number): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-user-project/${projectId}`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to GET request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error fetching SonarQube project users:', error)
    throw error
  }
}
export async function createSonarqubeProjectUser (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-user-project`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to POST request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error creating SonarQube project user:', error)
    throw error
  }
}
export async function getSonarqubePlatformUsers (platformId: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-organization/${platformId}/users`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to GET request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error fetching SonarQube platform users:', error)
    throw error
  }
}
export async function getSonarqubePlatformProjects (platformId: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-organization/${platformId}/projects`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to GET request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error fetching SonarQube platform projects:', error)
    throw error
  }
}
export async function getSonarqubeUsers (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/sonarqube-users`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to GET request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error fetching SonarQube users:', error)
    throw error
  }
}

export async function getAgents (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/agent`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getAgentsByRequestId (requestId: any): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/agent/request/${requestId}`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function createAgent (payload: Agent): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/agent`,
      method: 'POST',
      data: JSON.stringify(payload)
    })
    return response.json()
  } catch (error) {
    console.log(error)
  }
}

export async function updateAgent (payload: any, id: number): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/agent/${id}`

    const response = await apiCaller({
      url,
      method: 'PATCH',
      data: JSON.stringify(payload)
    })

    return response.json()
  } catch (error) {
    console.log('DB operator failed while fetching values', error)
    return {}
  }
}

export async function getGithubOrganizations (): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/githuborganization`,
      method: 'GET'
    })

    return response.json()
  } catch (error) {
    console.log('db operator for platform values call has failed', error)
    return []// api failure should not prevent page from loading
  }
}

export async function getBrowserstackProducts (): Promise<any> {
  try {
    const endpoints = [
      `${dbOperatorUrl}/browserstack-products/filter`,
      `${dbOperatorUrl}/browserstack-product`,
      `${dbOperatorUrl}/browserstack-products`
    ]

    for (const endpointUrl of endpoints) {
      try {
        const response = await apiCaller({
          url: endpointUrl,
          method: 'GET'
        })

        if (response.ok) {
          return response.json()
        }
      } catch (error) {
        console.log('db operator browserstack products endpoint failed', endpointUrl, error)
      }
    }

    return []
  } catch (error) {
    console.log('db operator for browserstack products call has failed', error)
    return []// api failure should not prevent page from loading
  }
}
export async function getProductsConsumption (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack/products/consumption`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching products consumption', error)
    throw error
  }
}

export async function getBrowserstackDashboard (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack/dashboard`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching browserstack dashboard', error)
    throw error
  }
}
export async function getBrowserstackProductsByTeam (teamId: number): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams/${teamId}/products`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching request', error)
    throw error
  }
}

/**
 * Represents a BrowserStack product with capacity information
 */
export interface BrowserStackProduct {
  id: string
  product_name: string
  product_code: string
  purchase_count: number
  allocated_count?: number
  status: 'ACTIVE' | 'INACTIVE' | string
}
/**
 * Fetches BrowserStack products via DB Operator endpoint.
 * @returns Promise containing array of BrowserStack products
 */
export async function fetchBrowserStackProducts (): Promise<BrowserStackProduct[]> {
  try {
    if (!dbOperatorUrl) {
      console.error('[BrowserStackProducts] DB_OPERATOR_FA_URL is not configured')
      return []
    }

    const endpoints = [
      `${dbOperatorUrl}/browserstack-products/filter`,
      `${dbOperatorUrl}/browserstack-product`,
      `${dbOperatorUrl}/browserstack-products`
    ]

    for (const endpointUrl of endpoints) {
      try {
        console.info('[BrowserStackProducts] Calling endpoint:', endpointUrl)

        const response = await apiCaller({
          url: endpointUrl,
          method: 'GET'
        })

        if (!response.ok) {
          console.warn(`[BrowserStackProducts] Endpoint failed: ${response.status} - ${response.statusText}`)
          continue
        }

        const data = await response.json()
        console.info('[BrowserStackProducts] Endpoint response status:', response.status)

        const products = extractBrowserStackProductsPayload(data)
        if (products.length > 0) {
          return validateProductArray(products)
        }

        console.warn('[BrowserStackProducts] Payload did not match expected shape for endpoint', endpointUrl, data)
      } catch (endpointError) {
        console.error('[BrowserStackProducts] Endpoint call failed', endpointUrl, endpointError)
      }
    }

    return []
  } catch (error) {
    console.error('API call to fetch BrowserStack products failed', error)
    return []
  }
}

function extractBrowserStackProductsPayload (payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload
  }

  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload)
      return extractBrowserStackProductsPayload(parsed)
    } catch (parseError) {
      console.error('Failed to parse BrowserStack products response string', parseError)
      return []
    }
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const maybeObject = payload as Record<string, unknown>

  if (Array.isArray(maybeObject.products)) {
    return maybeObject.products
  }

  if (Array.isArray(maybeObject.data)) {
    return maybeObject.data
  }

  if (Array.isArray(maybeObject.items)) {
    return maybeObject.items
  }

  if ('body' in maybeObject) {
    return extractBrowserStackProductsPayload(maybeObject.body)
  }

  return []
}

/**
 * Validates and normalizes the product array
 */
function validateProductArray (products: unknown[]): BrowserStackProduct[] {
  return products
    .filter((product): product is Record<string, unknown> => typeof product === 'object' && product !== null)
    .filter((product) => {
      // Ensure required fields are present
      const hasRequiredFields =
        'product_name' in product &&
        'product_code' in product &&
        'purchase_count' in product &&
        'status' in product

      if (!hasRequiredFields) {
        console.warn('Product missing required fields:', product)
      }

      return hasRequiredFields
    })
    .map((product, index) => ({
      id: 'id' in product ? String(product.id) : `${String(product.product_code)}-${index}`,
      product_name: String(product.product_name),
      product_code: String(product.product_code),
      purchase_count: Number(product.purchase_count),
      allocated_count: Number(product.allocated_count ?? 0),
      status: String(product.status).trim()
    }))
    .filter((product) => Number.isFinite(product.purchase_count) && Number.isFinite(product.allocated_count ?? 0))
    .map((product) => ({
      ...product,
      purchase_count: product.purchase_count,
      allocated_count: product.allocated_count ?? 0
    }))
}

export async function getBrowserstackProductsPrice (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-products/price`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching browserstack products price', error)
    throw error
  }
}

export async function updateBrowserstackTeamProducts (payload: { team_id: number, products: any[] }): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams/${payload.team_id}/products`,
      method: 'PATCH',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to update browserstack team products: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error updating browserstack team products:', error)
    throw error
  }
}

export async function getBrowserstackTeamUsers (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-team-users`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching team users', error)
    throw error
  }
}

export async function getBrowserstackTeamUsersByTeam (teamId: number): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams/${teamId}/users`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching team users by team', error)
    throw error
  }
}

export async function createBrowserstackTeamUser (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-user`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to POST request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error creating or updating browserstack team user:', error)
    throw error
  }
}

export async function updateBrowserstackUser (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-team-users`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to update browserstack user: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error updating browserstack user:', error)
    throw error
  }
}

export async function updateBrowserstackUserProduct (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-team-users`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to update browserstack user product accesses: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error updating browserstack user product accesses:', error)
    throw error
  }
}

export async function removeBrowserstackTeamUser (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams/${payload.team_id}/users`,
      method: 'DELETE',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to remove team user: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error removing browserstack team user:', error)
    throw error
  }
}

export async function getBrowserstackTeams (): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams`,
      method: 'GET',
      subscriptionType: 'all'
    })

    const hasError = response.error != null && response.error !== ''
    if (hasError || response.status !== 200) {
      throw new Error(`Failed to GET request: ${response.error ?? response.status}`)
    }

    return response.data
  } catch (error) {
    console.log('DB operator failed while fetching request', error)
    throw error
  }
}
export async function createBrowserstackTeam (payload: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams`,
      method: 'POST',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to POST request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error creating browserstack team:', error)
    throw error
  }
}
export async function updateBrowserstackTeam (id: any, payload: any): Promise<any> {
  try {
    const url = `${dbOperatorUrl}/browserstack-teams/${id}`

    const response = await secureApiCall({
      targetUrl: url,
      method: 'PATCH',
      subscriptionType: 'all',
      body: payload
    })

    const hasError = response?.error != null && response.error !== ''

    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to PATCH request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error updating browserstack-teams:', error)
    throw error
  }
}

export async function deleteBrowserstackTeam (id: any): Promise<any> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/browserstack-teams/${id}`,
      method: 'DELETE',
      subscriptionType: 'all'
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to DELETE request: ${response?.error ?? response?.status}`)
    }

    return response.data
  } catch (error) {
    console.error('Error deleting browserstack team:', error)
    throw error
  }
}

export async function createAihackTermsAcceptanceLog (payload: any): Promise<any> {
  try {
    const response = await apiCaller({
      url: `${dbOperatorUrl}/aihackTermsAcceptanceLog`,
      method: 'POST',
      data: JSON.stringify(payload)
    })
    return response.json()
  } catch (error) {
    console.log(error)
  }
}

export interface RequestData {
  payload: {
    id?: number
    request_id?: number
    [key: string]: any
  }
  [key: string]: any
}

interface SubmitRequestParams {
  requestType: string
  requestData: RequestData
  principal: string
  requesterEmail: string
  sessionId?: string
}

export async function submitRequestData (requestPayload: SubmitRequestParams): Promise<{ requestId: number, responseData: RequestData | null, error: unknown | null }> {
  try {
    const response = await secureApiCall({
      targetUrl: `${dbOperatorUrl}/request`,
      method: 'POST',
      subscriptionType: 'all',
      body: requestPayload
    })

    const hasError = response?.error != null && response.error !== ''
    if (hasError || response?.status !== 200) {
      throw new Error(`Failed to POST request: ${response?.error ?? response?.status}`)
    }
    if (response.data.id == null) {
      throw new Error('Invalid response from saveFormRequest - missing id')
    }
    const savedRequestId = Number(response.data.id)
    return {
      requestId: savedRequestId,
      responseData: response.data,
      error: null
    }
  } catch (error) {
    console.error('submit request data failed:', error)
    return {
      requestId: 0,
      responseData: null,
      error
    }
  }
}
